import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { createRoadmapNode, getAllRoadmapNodes } from '@/features/canvas/api';
import { getAllBudgetsForRoadmap } from '@/features/canvas/api/initiative_budgets';
import { FilteredNodes, Node, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useShowToast } from '@/hooks/useShowToast';

interface UseInitiativeDropProps {
  roadmapId: number;
}

export const useInitiativeDrop = ({ roadmapId }: UseInitiativeDropProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useShowToast();
  const {
    setFilteredNodes,
    filteredNodes,
    generateUniqueIntTemporaryId,
    updateLastSavedTime,
    setSelectedNodeId,
    setIsCreatingNewInitiative,
    temporaryCreatedIds,
  } = useEditorContext();

  const { mutateAsync: createNewRoadmapNode } = useMutation(
    ({ newNode }: { newNode: Node; newNodeTempId: number; newNodesInitiativeTempId: number }) =>
      createRoadmapNode({
        roadmapId,
        newNode,
      }),
    {
      onSuccess: ({ id, initiatives }, { newNodeTempId, newNodesInitiativeTempId }) => {
        queryClient.setQueryData(
          [getAllRoadmapNodes.name, roadmapId],
          (oldNodes?: FilteredNodes[]) => {
            if (!oldNodes) return oldNodes ?? [];
            return oldNodes.map(n => {
              if (n.id === newNodeTempId) {
                temporaryCreatedIds.delete(newNodeTempId);
                temporaryCreatedIds.delete(newNodesInitiativeTempId);
                return { ...n, id, initiatives };
              }
              return n;
            });
          }
        );
        updateLastSavedTime();
      },
    }
  );

  const onNewInitiativeDrop = useCallback(
    async ({
      title,
      newNodesCanvasXPercentage,
      newNodesCanvasYPercentage,
      timePeriodId,
      sectionId,
    }: {
      newNodesCanvasXPercentage: number;
      newNodesCanvasYPercentage: number;
      sectionId: number;
      timePeriodId: number;
      title: string;
    }) => {
      const newNodeTempId = generateUniqueIntTemporaryId();
      const newInitiativeTempId = generateUniqueIntTemporaryId();

      const newRoadmapNode = {
        id: newNodeTempId,
        title,
        isVisible: true,
        x: newNodesCanvasXPercentage,
        y: newNodesCanvasYPercentage,
        timePeriodId,
        sectionId,
        dependencies: [],
        initiatives: { title },
      };

      queryClient.setQueryData(
        [getAllRoadmapNodes.name, roadmapId],
        (oldNodes?: FilteredNodes[]) => {
          const newRoadmapNodeWithInitiative = {
            ...{ ...newRoadmapNode, shape: 'circle' as const, color: '' },
            initiatives: {
              id: newInitiativeTempId,
              title,
            },
          };
          if (!oldNodes) {
            setFilteredNodes([newRoadmapNodeWithInitiative] as unknown as typeof filteredNodes);
            return [newRoadmapNodeWithInitiative];
          }

          // Add to full dataset
          const newNodes: FilteredNodes[] = [...oldNodes, newRoadmapNodeWithInitiative];

          // Also update filteredNodes for canvas display
          setFilteredNodes([...filteredNodes, newRoadmapNodeWithInitiative]);

          return newNodes;
        }
      );

      try {
        const newlyCreatedNode = await createNewRoadmapNode({
          newNode: newRoadmapNode,
          newNodeTempId,
          newNodesInitiativeTempId: newInitiativeTempId,
        });
        const newInitiative = newlyCreatedNode.initiatives;

        setSelectedNodeId(newlyCreatedNode.id!);
        setIsCreatingNewInitiative(false);

        await Promise.all([
          queryClient.invalidateQueries([getAllBudgetsForRoadmap.name, roadmapId]),
          queryClient.invalidateQueries(['roadmap-budget-summary', roadmapId]),
          queryClient.invalidateQueries(['budget-by-time-period', roadmapId]),
          queryClient.invalidateQueries(['budget-by-section', roadmapId]),
        ]);

        return {
          initiative: newInitiative,
          nodeId: newlyCreatedNode.id!,
        };
      } catch (error) {
        showToast('error', t('editor.canvas.add_initiative_error'));
        await queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmapId]);
      }
    },
    [
      generateUniqueIntTemporaryId,
      queryClient,
      roadmapId,
      setFilteredNodes,
      filteredNodes,
      createNewRoadmapNode,
      setSelectedNodeId,
      setIsCreatingNewInitiative,
      showToast,
      t,
    ]
  );
  return {
    onNewInitiativeDrop,
  };
};
